/**
 * Neurula Reporting Dashboard
 * 
 * Main dashboard component for the reporting system. Provides:
 * - Overview of reports, templates, and recent activity
 * - Quick access to key reporting functions
 * - Statistics and performance metrics
 * - Clean medical-grade interface
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Col, Card, Button, Dropdown, Badge, Modal, Spinner, Alert } from 'react-bootstrap';
import './reporting.css';
import { 
    BsGrid, 
    BsList, 
    BsPlus, 
    BsFilter, 
    BsDownload, 
    BsSearch,
    BsGear,
    BsBarChart,
    BsFileText,
    BsStars,
    BsCloudDownload,
    BsEye,
    BsThreeDotsVertical
} from 'react-icons/bs';
import { toast } from 'react-hot-toast';

import {
    fetchReports,
    fetchTemplates,
    fetchSystemStats,
    fetchReportTypes,
    generateReportFromTemplate,
    setViewMode,
    setSortBy,
    setSearchQuery,
    setActiveModal,
    selectReports,
    selectTemplates,
    selectReportsLoading,
    selectTemplatesLoading,
    selectSystemStats,
    selectUI,
    selectViewMode
} from '../../redux/slices/reportingSlice';

// Import shared components
import { 
    StatsCard, 
    ReportCard, 
    TemplateCard, 
    QuickActions,
    ErrorBoundary,
    ServiceStatus,
    ReportingFallback
} from './shared';

// Import the new TemplateFilterModal
import TemplateFilterModal from './TemplateFilterModal';

const ReportingDashboard = () => {
    const dispatch = useDispatch();
    
    // Redux state
    const reports = useSelector(selectReports);
    const templates = useSelector(selectTemplates);
    const reportsLoading = useSelector(selectReportsLoading);
    const templatesLoading = useSelector(selectTemplatesLoading);
    const systemStats = useSelector(selectSystemStats);
    const ui = useSelector(selectUI);
    const viewMode = useSelector(selectViewMode);
    
    // Local state
    const [activeTab, setActiveTab] = useState('overview');
    const [searchTerm, setSearchTerm] = useState('');
    const [showQuickActions, setShowQuickActions] = useState(false);
    const [activeReports, setActiveReports] = useState([]);
    const [reportProgress, setReportProgress] = useState({});
    
    // Template filter modal state
    const [showTemplateFilterModal, setShowTemplateFilterModal] = useState(false);
    const [selectedTemplateForReport, setSelectedTemplateForReport] = useState(null);
    
    // Fetch initial data
    useEffect(() => {
        dispatch(fetchReports({ limit: 10 }));
        dispatch(fetchTemplates({ limit: 10 }));
        // Removed fetchSystemStats() - ServiceStatus components handle this
        dispatch(fetchReportTypes());
    }, [dispatch]);
    
    // Handle search
    const handleSearch = (term) => {
        setSearchTerm(term);
        dispatch(setSearchQuery(term));
        if (term.trim()) {
            dispatch(fetchReports({ search: term, limit: 10 }));
        } else {
            dispatch(fetchReports({ limit: 10 }));
        }
    };
    
    // Handle view mode change
    const handleViewModeChange = (mode) => {
        dispatch(setViewMode(mode));
    };
    
    // Handle sort change
    const handleSortChange = (field, order) => {
        dispatch(setSortBy({ field, order }));
        dispatch(fetchReports({ 
            sort_by: field, 
            sort_order: order,
            limit: 10 
        }));
    };
    
    // Quick action handlers
    const handleCreateReport = () => {
        dispatch(setActiveModal('create-report'));
    };
    
    const handleCreateTemplate = () => {
        dispatch(setActiveModal('create-template'));
    };
    
    const handleAIReport = () => {
        dispatch(setActiveModal('ai-report'));
    };
    
    // Handle template preview
    const handlePreviewTemplate = async (template) => {
        try {
            setShowQuickActions(true); // Temporarily reuse this modal for preview
            // In a real implementation, this would show a dedicated preview modal
            toast.success(`Previewing template: ${template.name}`);
        } catch (error) {
            toast.error('Failed to preview template');
        }
    };
    
    // Handle template selection for filtered report generation
    const handleUseTemplateWithFilters = (template) => {
        setSelectedTemplateForReport(template);
        setShowTemplateFilterModal(true);
    };

    // Handle report generation from template filter modal
    const handleGenerateReportFromTemplate = async (reportRequest) => {
        try {
            const response = await dispatch(generateReportFromTemplate(reportRequest));
            
            if (response.meta?.requestStatus === 'fulfilled') {
                toast.success(`Report generation started successfully`);
                
                // Refresh reports list
                dispatch(fetchReports({ limit: 10 }));
            } else {
                const errorMessage = response.error?.message || response.payload?.message || 'Failed to generate report';
                toast.error(errorMessage);
            }
        } catch (error) {
            toast.error('Failed to generate report');
        }
    };

    
    const stats = {
        totalReports: reports?.length || 0,
        totalTemplates: templates?.length || 0,
        reportsGenerated: systemStats?.reports_generated_today || 0,
        avgGenerationTime: systemStats?.avg_generation_time || '2.3s'
    };
    
    return (
        <ErrorBoundary fallbackMessage="Unable to load the reporting dashboard. Please refresh the page or contact support.">
            <ServiceStatus 
                fallbackComponent={<ReportingFallback />}
                showStatus={true}
                autoRetry={false}
            >
                <div className="reporting-dashboard">
                    {/* Header Section */}
                    <Container fluid className="p-4">
                        {/* Active Reports Progress Banner */}
                        {activeReports.length > 0 && (
                            <Row className="mb-3">
                                <Col>
                                    <Alert variant="info" className="mb-0">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center">
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                <span>
                                                    <strong>{activeReports.length}</strong> report{activeReports.length > 1 ? 's' : ''} currently generating...
                                                </span>
                                            </div>
                                            <Button 
                                                variant="link" 
                                                size="sm" 
                                                className="text-decoration-none p-0"
                                                onClick={() => setActiveTab('reports')}
                                            >
                                                View Progress
                                            </Button>
                                        </div>
                                    </Alert>
                                </Col>
                            </Row>
                        )}
                <Row className="align-items-center mb-4">
                    <Col lg={8}>
                        <div className="dashboard-header">
                            <h2 className="text-primary fw-bold mb-1">Reports & Analytics</h2>
                            <p className="text-muted mb-0">
                                Generate, manage, and analyze your healthcare reports
                            </p>
                        </div>
                    </Col>
                    <Col lg={4} className="text-end">
                        <div className="header-actions d-flex justify-content-end gap-2">
                            <Button 
                                variant="outline-primary"
                                size="sm"
                                onClick={() => setShowQuickActions(true)}
                            >
                                <BsGear className="me-1" />
                                Settings
                            </Button>
                            <Dropdown>
                                <Dropdown.Toggle variant="primary" size="sm">
                                    <BsPlus className="me-1" />
                                    New
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={handleCreateReport}>
                                        <BsFileText className="me-2" />
                                        New Report
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={handleCreateTemplate}>
                                        <BsBarChart className="me-2" />
                                        New Template
                                    </Dropdown.Item>
                                    <Dropdown.Divider />
                                    <Dropdown.Item onClick={handleAIReport}>
                                        <BsStars className="me-2" />
                                        AI Report
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </Col>
                </Row>
                
                {/* Stats Cards */}
                <Row className="mb-4">
                    <Col lg={3} md={6} className="mb-3">
                        <StatsCard
                            icon={BsFileText}
                            value={stats.totalReports}
                            description="Total Reports"
                            iconColor="primary"
                            isLoading={reportsLoading}
                        />
                    </Col>
                    <Col lg={3} md={6} className="mb-3">
                        <StatsCard
                            icon={BsBarChart}
                            value={stats.totalTemplates}
                            description="Templates"
                            iconColor="success"
                            isLoading={templatesLoading}
                        />
                    </Col>
                    <Col lg={3} md={6} className="mb-3">
                        <StatsCard
                            icon={BsCloudDownload}
                            value={stats.reportsGenerated}
                            description="Generated Today"
                            iconColor="info"
                            isLoading={reportsLoading}
                        />
                    </Col>
                    <Col lg={3} md={6} className="mb-3">
                        <StatsCard
                            icon={BsStars}
                            value={stats.avgGenerationTime}
                            description="Avg Generation"
                            iconColor="warning"
                            isLoading={reportsLoading}
                        />
                    </Col>
                </Row>
                
                {/* Navigation Tabs */}
                <Row className="mb-4">
                    <Col>
                        <div className="dashboard-tabs d-flex align-items-center justify-content-between">
                            <div className="tab-buttons">
                                <Button
                                    variant={activeTab === 'overview' ? 'primary' : 'outline-secondary'}
                                    size="sm"
                                    className="me-2"
                                    onClick={() => setActiveTab('overview')}
                                >
                                    Overview
                                </Button>
                                <Button
                                    variant={activeTab === 'reports' ? 'primary' : 'outline-secondary'}
                                    size="sm"
                                    className="me-2"
                                    onClick={() => setActiveTab('reports')}
                                >
                                    Reports ({reports?.length || 0})
                                </Button>
                                <Button
                                    variant={activeTab === 'templates' ? 'primary' : 'outline-secondary'}
                                    size="sm"
                                    className="me-2"
                                    onClick={() => setActiveTab('templates')}
                                >
                                    Templates ({templates?.length || 0})
                                </Button>
                            </div>
                            
                            <div className="view-controls d-flex align-items-center gap-2">
                                {/* Search */}
                                <div className="search-box position-relative">
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="Search reports..."
                                        value={searchTerm}
                                        onChange={(e) => handleSearch(e.target.value)}
                                        style={{ paddingLeft: '2.5rem', minWidth: '200px' }}
                                    />
                                    <BsSearch className="position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" />
                                </div>
                                
                                {/* Sort */}
                                <Dropdown>
                                    <Dropdown.Toggle variant="outline-secondary" size="sm">
                                        <BsFilter className="me-1" />
                                        Sort
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <Dropdown.Item onClick={() => handleSortChange('created_at', 'desc')}>
                                            Newest First
                                        </Dropdown.Item>
                                        <Dropdown.Item onClick={() => handleSortChange('created_at', 'asc')}>
                                            Oldest First
                                        </Dropdown.Item>
                                        <Dropdown.Item onClick={() => handleSortChange('name', 'asc')}>
                                            Name A-Z
                                        </Dropdown.Item>
                                        <Dropdown.Item onClick={() => handleSortChange('name', 'desc')}>
                                            Name Z-A
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                                
                                {/* View Mode */}
                                <div className="view-mode-toggle btn-group" role="group">
                                    <Button
                                        variant={viewMode === 'grid' ? 'primary' : 'outline-secondary'}
                                        size="sm"
                                        onClick={() => handleViewModeChange('grid')}
                                    >
                                        <BsGrid />
                                    </Button>
                                    <Button
                                        variant={viewMode === 'list' ? 'primary' : 'outline-secondary'}
                                        size="sm"
                                        onClick={() => handleViewModeChange('list')}
                                    >
                                        <BsList />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>
                
                {/* Content Area */}
                <Row>
                    <Col>
                        {activeTab === 'overview' && (
                            <div className="overview-content">
                                <Row>
                                    {/* Recent Reports */}
                                    <Col lg={6} className="mb-4">
                                        <Card className="border-0 shadow-sm h-100">
                                            <Card.Header className="bg-transparent border-0 pb-0">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <h5 className="mb-0 fw-semibold">Recent Reports</h5>
                                                    <Button variant="link" size="sm" className="text-decoration-none">
                                                        View All
                                                    </Button>
                                                </div>
                                            </Card.Header>
                                            <Card.Body>
                                                {reportsLoading ? (
                                                    <div className="text-center py-4">
                                                        <Spinner animation="border" size="sm" className="text-primary" />
                                                        <p className="text-muted mt-2 mb-0">Loading reports...</p>
                                                    </div>
                                                ) : reports?.length > 0 ? (
                                                    <div className="reports-list">
                                                        {reports.slice(0, 3).map((report, index) => (
                                                            <div key={report.id || index} className="mb-2">
                                                                <ReportCard
                                                                    report={report}
                                                                    viewMode="list"
                                                                />
                                                            </div>
                                                        ))}
                                                        {reports.length > 3 && (
                                                            <div className="text-center mt-3">
                                                                <Button 
                                                                    variant="link" 
                                                                    size="sm"
                                                                    onClick={() => setActiveTab('reports')}
                                                                >
                                                                    View {reports.length - 3} more reports
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="text-center py-4">
                                                        <BsFileText className="display-4 text-muted mb-3" />
                                                        <p className="text-muted">No reports yet</p>
                                                        <Button variant="primary" size="sm" onClick={handleCreateReport}>
                                                            Create Your First Report
                                                        </Button>
                                                    </div>
                                                )}
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    
                                    {/* Quick Generate Templates */}
                                    <Col lg={6} className="mb-4">
                                        <Card className="border-0 shadow-sm h-100">
                                            <Card.Header className="bg-transparent border-0 pb-0">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <h5 className="mb-0 fw-semibold">Quick Generate Reports</h5>
                                                    <Button 
                                                        variant="link" 
                                                        size="sm" 
                                                        className="text-decoration-none"
                                                        onClick={() => setActiveTab('templates')}
                                                    >
                                                        View All Templates
                                                    </Button>
                                                </div>
                                            </Card.Header>
                                            <Card.Body>
                                                {templatesLoading ? (
                                                    <div className="text-center py-4">
                                                        <Spinner animation="border" size="sm" className="text-primary" />
                                                        <p className="text-muted mt-2 mb-0">Loading templates...</p>
                                                    </div>
                                                ) : templates?.length > 0 ? (
                                                    <div className="quick-templates-list">
                                                        {templates.slice(0, 4).map((template, index) => (
                                                            <div key={template.id || index} className="template-quick-card mb-2 p-3 border rounded bg-light">
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <div className="flex-grow-1">
                                                                        <div className="d-flex align-items-center mb-1">
                                                                            <BsBarChart className="text-primary me-2" />
                                                                            <h6 className="mb-0 fw-semibold">{template.name}</h6>
                                                                        </div>
                                                                        <div className="d-flex align-items-center gap-2">
                                                                            <Badge bg="primary" size="sm">
                                                                                {template.category}
                                                                            </Badge>
                                                                            <small className="text-muted">
                                                                                {Array.isArray(template.available_filters) ? template.available_filters.length : 0} filters
                                                                            </small>
                                                                        </div>
                                                                    </div>
                                                                    <div className="d-flex gap-1">
                                                                        <Button 
                                                                            variant="outline-primary" 
                                                                            size="sm"
                                                                            onClick={() => handlePreviewTemplate(template)}
                                                                            title="Preview template"
                                                                        >
                                                                            <BsEye />
                                                                        </Button>
                                                                        <Button 
                                                                            variant="primary" 
                                                                            size="sm"
                                                                            onClick={() => handleUseTemplateWithFilters(template)}
                                                                            title="Generate report now"
                                                                        >
                                                                            <BsDownload />
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                        {templates.length > 4 && (
                                                            <div className="text-center mt-3">
                                                                <Button 
                                                                    variant="outline-primary" 
                                                                    size="sm"
                                                                    onClick={() => setActiveTab('templates')}
                                                                >
                                                                    <BsBarChart className="me-1" />
                                                                    View All {templates.length} Templates
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="text-center py-4">
                                                        <BsBarChart className="display-4 text-muted mb-3" />
                                                        <p className="text-muted">No templates available</p>
                                                        <p className="small text-muted mb-3">Create templates to enable quick report generation</p>
                                                        <Button variant="success" size="sm" onClick={handleCreateTemplate}>
                                                            <BsPlus className="me-1" />
                                                            Create Your First Template
                                                        </Button>
                                                    </div>
                                                )}
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                            </div>
                        )}
                        
                        {activeTab === 'reports' && (
                            <div className="reports-content">
                                {reportsLoading ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" className="text-primary" />
                                        <p className="text-muted mt-2">Loading reports...</p>
                                    </div>
                                ) : reports?.length > 0 ? (
                                    <div className={`reports-grid ${viewMode === 'list' ? 'list-view' : 'grid-view'}`}>
                                        <Row className={viewMode === 'grid' ? 'g-3' : 'g-2'}>
                                            {reports.map((report, index) => (
                                                <Col key={report.id || index} lg={viewMode === 'grid' ? 4 : 12} md={viewMode === 'grid' ? 6 : 12}>
                                                    <ReportCard
                                                        report={report}
                                                        viewMode={viewMode}
                                                    />
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                ) : (
                                    <div className="text-center py-5">
                                        <BsFileText className="display-4 text-muted mb-3" />
                                        <h5 className="text-muted">No Reports Found</h5>
                                        <p className="text-muted mb-4">
                                            You haven't generated any reports yet. Create your first report to get started.
                                        </p>
                                        <Button variant="primary" onClick={handleCreateReport}>
                                            <BsPlus className="me-1" />
                                            Generate Report
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                        
                        {activeTab === 'templates' && (
                            <div className="templates-content">
                                {templatesLoading ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" className="text-primary" />
                                        <p className="text-muted mt-2">Loading templates...</p>
                                    </div>
                                ) : templates?.length > 0 ? (
                                    <div className={`templates-grid ${viewMode === 'list' ? 'list-view' : 'grid-view'}`}>
                                        <Row className={viewMode === 'grid' ? 'g-3' : 'g-2'}>
                                            {templates.map((template, index) => (
                                                <Col key={template.id || index} lg={viewMode === 'grid' ? 4 : 12} md={viewMode === 'grid' ? 6 : 12}>
                                                    <TemplateCard
                                                        template={template}
                                                        viewMode={viewMode}
                                                        onPreview={handlePreviewTemplate}
                                                        onUse={handleUseTemplateWithFilters}
                                                    />
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                ) : (
                                    <div className="text-center py-5">
                                        <BsBarChart className="display-4 text-muted mb-3" />
                                        <h5 className="text-muted">No Templates Found</h5>
                                        <p className="text-muted mb-4">
                                            No report templates are available. Create a template to get started.
                                        </p>
                                        <Button variant="success" onClick={handleCreateTemplate}>
                                            <BsPlus className="me-1" />
                                            Create Template
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </Col>
                </Row>
            </Container>
            
            {/* Quick Actions Modal */}
            <Modal show={showQuickActions} onHide={() => setShowQuickActions(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Quick Actions</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="quick-actions-grid">
                        <div className="row g-3">
                            <div className="col-6">
                                <Button 
                                    variant="outline-primary" 
                                    className="w-100 p-3 h-100"
                                    onClick={handleCreateReport}
                                >
                                    <BsFileText className="d-block mb-2 h4" />
                                    <span>New Report</span>
                                </Button>
                            </div>
                            <div className="col-6">
                                <Button 
                                    variant="outline-success" 
                                    className="w-100 p-3 h-100"
                                    onClick={handleCreateTemplate}
                                >
                                    <BsBarChart className="d-block mb-2 h4" />
                                    <span>New Template</span>
                                </Button>
                            </div>
                            <div className="col-6">
                                <Button 
                                    variant="outline-warning" 
                                    className="w-100 p-3 h-100"
                                    onClick={handleAIReport}
                                >
                                    <BsStars className="d-block mb-2 h4" />
                                    <span>AI Report</span>
                                </Button>
                            </div>
                            <div className="col-6">
                                <Button 
                                    variant="outline-info" 
                                    className="w-100 p-3 h-100"
                                >
                                    <BsDownload className="d-block mb-2 h4" />
                                    <span>Export Data</span>
                                </Button>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Template Filter Modal */}
            <TemplateFilterModal
                show={showTemplateFilterModal}
                onHide={() => {
                    setShowTemplateFilterModal(false);
                    setSelectedTemplateForReport(null);
                }}
                template={selectedTemplateForReport}
                onGenerateReport={handleGenerateReportFromTemplate}
            />
                </div>
            </ServiceStatus>
        </ErrorBoundary>
    );
};

export default ReportingDashboard;